import React, { useMemo, ReactNode } from "react";
import { Component, Viewport, CanvasSettings } from "@/types";
import { cn } from "@/lib/utils";
import { useInteractionContext } from "./InteractionContextRuntime";
import {
  getViewportDimensions,
  useCanvasBackgroundStyle,
  useCanvasLayoutStyle,
  useContentHeight,
} from "./SharedCanvasCoreUtils";

// Props for the shared canvas core
export interface SharedCanvasCoreProps {
  canvasSettings: CanvasSettings;
  viewport: Viewport;
  components: Component[];
  zoom?: number;
  className?: string;
  children?: ReactNode;
  spatialIndex?: unknown; // Will be typed to SpatialIndexingSystem when integrated
  onCanvasInteraction?: (type: string, data: unknown) => void;
  layoutMode?: 'editor' | 'viewer'; // New prop for layout behavior
}

// Grid component that can be shared between editor and viewer
export const SharedGrid: React.FC<{
  enabled: boolean;
  size: number;
  zoom: number;
  settings?: CanvasSettings["grid"];
}> = ({ enabled, size, zoom, settings }) => {
  if (!enabled || !settings?.enabled) return null;

  const adjustedSize = (settings?.size || size) * zoom;
  const majorLineInterval = settings?.majorLines?.interval || 5;
  const adjustedMajorSize = adjustedSize * majorLineInterval;

  // Convert hex color to rgba
  const hexToRgba = (hex: string, opacity: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const gridColor = settings?.color || "#e5e7eb";
  const gridOpacity = settings?.opacity || 0.5;
  const majorLineColor = settings?.majorLines?.color || "#d1d5db";
  const majorLineOpacity = settings?.majorLines?.opacity || 0.7;

  const gridColorRgba = hexToRgba(gridColor, gridOpacity);
  const majorLineColorRgba = hexToRgba(majorLineColor, majorLineOpacity);

  // Create grid pattern based on style
  const createGridPattern = (color: string, lineStyle: string): string => {
    if (lineStyle === "dashed") {
      return `repeating-linear-gradient(to right, ${color} 0px, ${color} 2px, transparent 2px, transparent 4px),
              repeating-linear-gradient(to bottom, ${color} 0px, ${color} 2px, transparent 2px, transparent 4px)`;
    } else if (lineStyle === "dotted") {
      return `radial-gradient(circle at 0 0, ${color} 1px, transparent 1px)`;
    } else {
      return `linear-gradient(to right, ${color} 1px, transparent 1px),
              linear-gradient(to bottom, ${color} 1px, transparent 1px)`;
    }
  };

  return (
    <>
      {/* Main grid */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: createGridPattern(
            gridColorRgba,
            settings?.style || "solid"
          ),
          backgroundSize:
            settings?.style === "dotted"
              ? `${adjustedSize}px ${adjustedSize}px`
              : `${adjustedSize}px ${adjustedSize}px`,
        }}
      />

      {/* Major grid lines */}
      {settings?.majorLines?.enabled && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: createGridPattern(
              majorLineColorRgba,
              settings?.style || "solid"
            ),
            backgroundSize: `${adjustedMajorSize}px ${adjustedMajorSize}px`,
          }}
        />
      )}
    </>
  );
};

// Main SharedCanvasCore component
export const SharedCanvasCore: React.FC<SharedCanvasCoreProps> = ({
  canvasSettings,
  viewport,
  components,
  zoom = 1,
  className,
  children,
  spatialIndex: _spatialIndex,
  onCanvasInteraction,
  layoutMode,
}) => {
  const interactionContext = useInteractionContext();
  const backgroundStyle = useCanvasBackgroundStyle(canvasSettings);
  const baseLayoutStyle = useCanvasLayoutStyle(canvasSettings);

  // Determine effective layout mode (explicit prop or derived from interaction context)
  const effectiveLayoutMode = layoutMode || (interactionContext.mode === "viewing" ? 'viewer' : 'editor');

  // Apply layout styles based on mode
  const layoutStyle = useMemo(() => {
    if (effectiveLayoutMode === 'viewer') {
      return {
        padding: 0, // Remove padding in viewer mode
        width: '100%', // Full width for viewer
        maxWidth: 'none',
        margin: 0,
      };
    }
    // Editor mode keeps original styles
    return baseLayoutStyle;
  }, [baseLayoutStyle, effectiveLayoutMode]);

  const contentHeight = useContentHeight(components);
  const viewportDimensions = getViewportDimensions(viewport);

  // Handle canvas click (deselect components in edit mode)
  const handleCanvasClick = (e: React.MouseEvent) => {
    // Only handle if clicking directly on canvas (not on a component)
    if (e.target === e.currentTarget && interactionContext.mode === "editing") {
      onCanvasInteraction?.("canvas-click", { position: { x: e.clientX, y: e.clientY } });
    }
  };

  // Determine grid visibility based on mode and settings
  const showGrid = useMemo(() => {
    // In viewing mode, only show grid if explicitly enabled in settings
    if (interactionContext.mode === "viewing") {
      return false; // Never show grid in pure viewing mode
    }
    // In editing or functional modes, respect the grid settings
    return canvasSettings?.grid?.enabled || false;
  }, [interactionContext.mode, canvasSettings?.grid?.enabled]);

  return (
    <div
      className={cn(
        "shared-canvas-core relative",
        interactionContext.mode === "viewing"
          ? "w-full"
          : "w-full h-full",
        "focus:outline-none",
        canvasSettings?.theme?.colorScheme === "dark" && "dark",
        className
      )}
      style={{
        ...backgroundStyle,
        ...layoutStyle,
        minHeight: effectiveLayoutMode === 'viewer' ? 'auto' : contentHeight,
        overflow: "visible",
      }}
      onClick={handleCanvasClick}
      data-viewport={viewport}
      data-interaction-mode={interactionContext.mode}
      role="main"
      aria-label={`Canvas in ${interactionContext.mode} mode`}
    >
      {/* Canvas content container with zoom - mode-aware dimensions */}
      <div
        className="relative"
        style={{
          transform: zoom !== 1 ? `scale(${zoom})` : 'none',
          transformOrigin: "top left",
          width: effectiveLayoutMode === 'viewer' ? '100%' : viewportDimensions.width,
          height: effectiveLayoutMode === 'viewer' ? 'auto' : `${contentHeight}px`,
          minHeight: effectiveLayoutMode === 'viewer' ? 'auto' : contentHeight,
          position: "relative",
        }}
      >
        {/* Grid layer */}
        {showGrid && (
          <SharedGrid
            enabled={showGrid}
            size={canvasSettings?.grid?.size || 20}
            zoom={zoom}
            settings={canvasSettings?.grid}
          />
        )}

        {/* Empty state */}
        {components.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center text-muted-foreground">
              <div className="text-lg font-medium mb-2">
                {interactionContext.mode === "editing"
                  ? "Drop components here to start designing"
                  : "No components to display"}
              </div>
              <div className="text-sm">
                {interactionContext.mode === "editing"
                  ? "Drag components from the palette or create new ones"
                  : "This project appears to be empty"}
              </div>
            </div>
          </div>
        )}

        {/* Component rendering area */}
        {children}
      </div>
    </div>
  );
};

// Viewport container for responsive preview
export const ViewportContainer: React.FC<{
  viewport: Viewport;
  zoom: number;
  children: ReactNode;
  className?: string;
}> = ({ viewport, zoom, children, className }) => {
  const dimensions = getViewportDimensions(viewport);

  return (
    <div
      className={cn(
        "viewport-container relative bg-white shadow-lg",
        className
      )}
      style={{
        width: dimensions.width * zoom,
        height: dimensions.height * zoom,
        transform: `scale(${zoom})`,
        transformOrigin: "top left",
      }}
      data-viewport={viewport}
    >
      {children}
    </div>
  );
};

// Export utilities and components
export default SharedCanvasCore;
